import { useEffect, useRef } from "react";

/**
 * What Escape can mean on a screen, nearest first.
 *
 * The order is the list, so it is read and changed in one place. Layered window listeners
 * answer in registration order, which is mount order, which is an accident: a popover
 * opened during a placement registers after it and would answer second, cancelling the
 * placement the attorney only meant to keep.
 */
const SCOPES = ["popover", "gesture", "fullscreen"] as const;

export type EscapeScope = (typeof SCOPES)[number];

interface Live {
  scope: EscapeScope;
  act: () => void;
}

/**
 * The live scopes in the order they became live, so an equal scope can be given to the
 * one that arrived last. A set rather than a list because a scope removes itself by
 * identity: nothing has to find its position, and a removal that misses cannot take some
 * other scope's place instead.
 */
const live = new Set<Live>();

const nearness = (scope: EscapeScope) => SCOPES.indexOf(scope);

/** The one scope that answers this press: nearest, and among equals the newest. */
function innermost(): Live | undefined {
  let chosen: Live | undefined;
  for (const candidate of live) {
    if (
      chosen === undefined ||
      nearness(candidate.scope) <= nearness(chosen.scope)
    ) {
      chosen = candidate;
    }
  }
  return chosen;
}

function pressed(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  // A held key repeats about thirty times a second. Answering each repeat would unwind
  // the whole stack on one press, one scope per repeat.
  if (event.repeat) return;
  innermost()?.act();
}

/**
 * One listener for every scope rather than one each, so the answer is chosen from the
 * whole stack instead of raced for by its members.
 */
function watch(entry: Live) {
  if (live.size === 0) window.addEventListener("keydown", pressed);
  live.add(entry);
  return () => {
    live.delete(entry);
    if (live.size === 0) window.removeEventListener("keydown", pressed);
  };
}

/**
 * Claim Escape for as long as this thing is live, and only when nothing nearer is.
 *
 * One press does one thing. `act` is read when the key is pressed rather than captured
 * when the scope opened, so a caller may hand a fresh closure on every render without
 * being pushed to the top of its own scope.
 */
export function useEscapeScope(
  scope: EscapeScope,
  active: boolean,
  act: () => void,
) {
  const actRef = useRef(act);
  useEffect(() => {
    actRef.current = act;
  });

  useEffect(() => {
    if (!active) return undefined;
    return watch({ scope, act: () => actRef.current() });
  }, [scope, active]);
}
